<template>
    <div class="form-field">
        <div class="form-field__input">
            <label
                v-if="label"
                :for="id"
                class="form-field__label"
                :class="{ 'is-active': isLabelActive }"
            >
                {{ label }}
            </label>

            <textarea
                :id="id"
                :required="required"
                :placeholder="placeholder"
                :class="[
                    'textarea-field',
                    { 'textarea-field_error': errorMessage },
                    { 'has-label': label }
                ]"
                :value="modelValue"
                :style="{
                    resize: resizable ? 'vertical' : 'none',
                    height: `${height ? height : 150}px`,
                }"
                @input="onInput"
                @focus="isLabelActive = true"
                @blur="isLabelActive = !!modelValue"
            ></textarea>

            <div v-if="maxLength" class="form-field__char-counter">
                {{ charCount }}/{{ maxLength }}
            </div>
        </div>

        <span v-if="errorMessage" class="form-field__error">
            {{ errorMessage }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, defineProps, defineEmits, watch } from 'vue';

    const props = defineProps({
        id: String,
        required: {
            type: Boolean,
            default: false,
        },
        label: String,
        placeholder: String,
        modelValue: String,
        errorMessage: String,
        height: {
            type: [Number, null],
            default: null,
        },
        maxLength: {
            type: [Number, null],
            default: 1100,
        },
        resizable: {
            type: Boolean,
            default: true,
        },
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void,
    }>();

    const isLabelActive = ref(!!props.modelValue);

    const charCount = computed(() => props.modelValue?.length || 0);

    watch(
        () => props.modelValue,
        (newValue) => {
            isLabelActive.value = !!newValue;
        },
    );

    function onInput(event: Event): void {
        const inputElement = event.target as HTMLTextAreaElement;
        let value = inputElement.value;

        if (props.maxLength) {
            value = value.slice(0, props.maxLength);
        }

        inputElement.value = value;
        emit('update:modelValue', value);
    }
</script>

<style lang="scss" scoped>
    @use './UITextArea.scss' as *;
</style>
